import React from "react";
import { Box, Typography, Stack, Avatar } from "@mui/material";
import { PlayArrowRounded, StarRounded, GroupsRounded } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getCourseImageUrl } from "utils/courseUtils";

/**
 * Full-bleed image "poster" card for courses & tutorials.
 * The cover image fills the entire card; title / instructor / meta are
 * overlaid on a gradient scrim. Premium hover: image zoom, lift, accent glow.
 *
 * @param {Object} course   - course/tutorial object
 * @param {Function} onClick - click handler (receives course)
 * @param {string} type     - "courses" | "tutorials"
 * @param {number} index    - for stagger animation
 * @param {string} aspectRatio - CSS aspect-ratio (default poster 4/5)
 */
const CURRENCY_SYMBOL = { USD: "$", EUR: "€", GBP: "£", RON: "lei" };

const CoursePosterCard = ({ course, onClick, type = "courses", index = 0, aspectRatio = "1 / 1.2" }) => {
  const { t } = useTranslation();
  const image = getCourseImageUrl(course);
  const progress = course.progress !== undefined ? Math.round(course.progress) : null;
  const isComplete = progress >= 100;
  const isFree = course.price === 0 || type === "tutorials";
  // The API returns flat fields (averageRating / enrollmentCount); some callers
  // pass a pre-shaped `stats` object — support both so meta always renders.
  const ratingVal = course.averageRating ?? course.stats?.rating ?? 0;
  const rating = ratingVal > 0 ? ratingVal : null;
  const studentsVal = course.enrollmentCount ?? course.stats?.students ?? 0;
  const students = studentsVal > 0 ? studentsVal : null;
  const lessonCount = Array.isArray(course.lessons)
    ? course.lessons.length
    : course.lessonCount || course.totalLessons || null;
  const currency = CURRENCY_SYMBOL[course.currency] || "$";
  const priceLabel = isFree
    ? t("tutorial_page.free_badge", "FREE")
    : currency === "lei"
    ? `${course.price} lei`
    : `${currency}${course.price}`;
  const level = course.level || null;
  const teacherName = course.teacher?.name || t("common.instructor", "Instructor");
  const teacherAvatar = course.teacher?.avatar?.url || course.teacher?.avatar || "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      style={{ height: "100%" }}
    >
      <Box
        onClick={() => onClick?.(course)}
        sx={{
          position: "relative",
          height: "100%",
          aspectRatio,
          borderRadius: "20px",
          overflow: "hidden",
          cursor: "pointer",
          background: "var(--bg-panel)",
          border: "1px solid var(--border-subtle)",
          boxShadow: "0 8px 26px -10px rgba(0,0,0,0.6)",
          transition: "transform 0.45s cubic-bezier(0.16,1,0.3,1), box-shadow 0.45s ease, border-color 0.45s ease",
          "&:hover": {
            transform: "translateY(-8px)",
            borderColor: "var(--border-hover)",
            boxShadow: "0 30px 56px -18px var(--accent-glow), 0 10px 24px -12px rgba(0,0,0,0.7)",
            "& .poster-img": { transform: "scale(1.07)" },
            "& .poster-play": { opacity: 1, transform: "translate(-50%,-50%) scale(1)" },
          },
        }}
      >
        {/* Full-bleed image */}
        <Box
          className="poster-img"
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transition: "transform 0.7s cubic-bezier(0.16,1,0.3,1)",
          }}
        />

        {/* Scrim for legibility */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(8,6,15,0.35) 0%, transparent 32%, transparent 48%, rgba(8,6,15,0.78) 88%, rgba(8,6,15,0.92) 100%)",
            pointerEvents: "none",
          }}
        />

        {/* Top chips */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ position: "absolute", top: 12, left: 12, right: 12, zIndex: 2 }}
        >
          {course.category ? (
            <Box
              sx={{
                px: 1.1,
                py: 0.4,
                borderRadius: "8px",
                background: "rgba(8,6,15,0.45)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.14)",
              }}
            >
              <Typography sx={{ color: "#fff", fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'Inter', sans-serif" }}>
                {course.category}
              </Typography>
            </Box>
          ) : <Box />}

          <Box
            sx={{
              px: 1.1,
              py: 0.4,
              borderRadius: "8px",
              background: isFree ? "var(--accent-gradient)" : "rgba(8,6,15,0.55)",
              backdropFilter: isFree ? "none" : "blur(10px)",
              border: isFree ? "none" : "1px solid rgba(255,255,255,0.14)",
              boxShadow: isFree ? "0 4px 14px -3px var(--accent-glow)" : "none",
            }}
          >
            <Typography sx={{ color: "#fff", fontSize: "0.66rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'Inter', sans-serif" }}>
              {priceLabel}
            </Typography>
          </Box>
        </Stack>

        {/* Center play on hover */}
        <Box
          className="poster-play"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%) scale(0.85)",
            zIndex: 2,
            width: 58,
            height: 58,
            borderRadius: "50%",
            background: "var(--accent-gradient)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0,
            transition: "all 0.4s cubic-bezier(0.16,1,0.3,1)",
            boxShadow: "0 10px 30px -4px var(--accent-glow)",
          }}
        >
          <PlayArrowRounded sx={{ color: "#fff", fontSize: 32, ml: 0.4 }} />
        </Box>

        {/* Bottom content overlay */}
        <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, zIndex: 2, p: 2 }}>
          <Typography
            title={course.title}
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontFamily: "'Space Grotesk', 'Inter', sans-serif",
              fontSize: "1.02rem",
              lineHeight: 1.25,
              letterSpacing: "-0.01em",
              textShadow: "0 2px 12px rgba(0,0,0,0.5)",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              mb: 1,
            }}
          >
            {course.title || t("common.untitled", "Untitled")}
          </Typography>

          {(lessonCount || level) && (
            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1 }}>
              {lessonCount > 0 && (
                <Typography sx={{ color: "rgba(255,255,255,0.62)", fontSize: "0.72rem", fontWeight: 500, fontFamily: "'Inter', sans-serif" }}>
                  {lessonCount} {t("catalog.lessons", lessonCount === 1 ? "lesson" : "lessons")}
                </Typography>
              )}
              {lessonCount > 0 && level && (
                <Box sx={{ width: 3, height: 3, borderRadius: "50%", background: "rgba(255,255,255,0.4)" }} />
              )}
              {level && (
                <Typography sx={{ color: "rgba(255,255,255,0.62)", fontSize: "0.72rem", fontWeight: 500, fontFamily: "'Inter', sans-serif", textTransform: "capitalize" }}>
                  {t(`catalog.level_${level}`, level)}
                </Typography>
              )}
            </Stack>
          )}

          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={0.9} sx={{ minWidth: 0 }}>
              <Avatar src={teacherAvatar} sx={{ width: 22, height: 22, fontSize: "0.6rem", border: "1px solid rgba(255,255,255,0.25)" }}>
                {teacherName.charAt(0)}
              </Avatar>
              <Typography sx={{ color: "rgba(255,255,255,0.82)", fontSize: "0.76rem", fontWeight: 500, fontFamily: "'Inter', sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {teacherName}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={0.6} alignItems="center" sx={{ flexShrink: 0 }}>
              {rating && (
                <Stack direction="row" alignItems="center" spacing={0.3}>
                  <StarRounded sx={{ fontSize: 14, color: "#fbbf24" }} />
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "0.72rem", fontFamily: "'Inter', sans-serif" }}>{rating.toFixed(1)}</Typography>
                </Stack>
              )}
              {students && (
                <Stack direction="row" alignItems="center" spacing={0.3}>
                  <GroupsRounded sx={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }} />
                  <Typography sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 600, fontSize: "0.72rem", fontFamily: "'Inter', sans-serif" }}>{students}</Typography>
                </Stack>
              )}
            </Stack>
          </Stack>

          {/* Progress (enrolled) */}
          {progress !== null && (
            <Box sx={{ mt: 1.4 }}>
              <Box sx={{ position: "relative", height: 5, borderRadius: 999, background: "rgba(255,255,255,0.16)", overflow: "hidden" }}>
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: `${progress}%`,
                    borderRadius: 999,
                    background: isComplete ? "linear-gradient(90deg,#10b981,#34d399)" : "var(--accent-gradient)",
                    boxShadow: isComplete ? "none" : "0 0 12px -2px var(--accent-glow)",
                    transition: "width 0.6s cubic-bezier(0.16,1,0.3,1)",
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </motion.div>
  );
};

export default CoursePosterCard;
